import React, { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, RegularPolygon, Text, Group } from 'react-konva';

const CanvasTableMap = ({ tables, onTableUpdate, onTableSelect, role, selectedTableId }) => {
    const canEdit = ['owner', 'manager'].includes(role);

    // Measure the actual container width (NOT the raw viewport) so the stage never
    // pushes the page — and therefore the app sidebar — off screen. The wrapper is
    // the scroll boundary; the stage simply fills whatever width it is given.
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(900);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            if (w) setContainerWidth(w);
        };
        measure();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        }
        window.addEventListener('resize', measure);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    const handleDragEnd = (e, table) => {
        if (!canEdit) return;
        const x = e.target.x();
        const y = e.target.y();
        onTableUpdate({ ...table, x, y });
    };

    // Stage fills the container, but grows to fit any saved table positioned beyond it
    // so outlying tables stay reachable — the excess scrolls INSIDE .canvas-wrapper
    // (which has overflow:auto + min-width:0) and never pushes the page/sidebar.
    const maxExtentX = tables.reduce((m, t) => Math.max(m, t.x || 0), 0);
    const stageWidth = Math.max(containerWidth, maxExtentX + 140);
    const stageHeight = Math.max(500, window.innerHeight - 250); // Larger and dynamic

    return (
        <div ref={containerRef} className="canvas-stage-host">
        <Stage width={stageWidth} height={stageHeight} style={{ border: '2px solid var(--border)', borderRadius: 'var(--r-lg)', background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', display: 'block' }}>
            <Layer>
                {tables.map((table) => (
                    <TableNode
                        key={table.id}
                        table={table}
                        draggable={canEdit && !table.is_locked}
                        selectedTableId={selectedTableId}
                        canEdit={canEdit}
                        onDragEnd={(e) => handleDragEnd(e, table)}
                        onClick={(e) => {
                            if (onTableSelect) {
                                // Get stage position for popup placement
                                const stage = e.target.getStage();
                                const pointerPos = stage.getPointerPosition();
                                // Offset popup to appear beside the table
                                onTableSelect(table, {
                                    x: pointerPos.x + 50,
                                    y: pointerPos.y + 180 // Account for toolbar offset
                                });
                            }
                        }}
                        stageWidth={stageWidth}
                        stageHeight={stageHeight}
                    />
                ))}
            </Layer>
        </Stage>
        </div>
    );
};

const TableNode = ({ table, draggable, onDragEnd, onClick, selectedTableId, stageWidth, stageHeight }) => {
    const isSelected = selectedTableId === table.id;
    const isLocked = table.is_locked === 1;

    // Shape types
    const isCircle = table.shape === 'circle';
    const isTriangle = table.shape === 'triangle';
    const isRectangle = table.shape === 'rectangle';  // Wide table
    const isSquare = table.shape === 'square' || (!isCircle && !isTriangle && !isRectangle);

    // Color logic (aligned with the brand/design tokens)
    let fill = '#eceef3'; // neutral default
    if (isLocked) fill = '#e7d6d6'; // muted grey-red = locked/unavailable
    if (isSelected) fill = '#fbe9f1'; // light brand tint = selected

    const stroke = isSelected ? '#d63384' : '#cfd2db'; // brand ring when selected, else soft border
    const strokeWidth = isSelected ? 2.5 : 1;

    // Constraint function
    const dragBoundFunc = (pos) => {
        const r = 30; // approx radius/offset
        const padding = 10;
        let newX = pos.x;
        let newY = pos.y;

        // Min bounds
        if (newX < padding + r) newX = padding + r;
        if (newY < padding + r) newY = padding + r;

        // Max bounds
        if (newX > stageWidth - padding - r) newX = stageWidth - padding - r;
        if (newY > stageHeight - padding - r) newY = stageHeight - padding - r;

        return { x: newX, y: newY };
    };

    return (
        <Group
            x={table.x}
            y={table.y}
            draggable={draggable}
            dragBoundFunc={dragBoundFunc}
            onDragEnd={onDragEnd}
            onClick={onClick}
            onTap={onClick}
        >
            {isCircle && (
                <Circle
                    radius={30}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                />
            )}
            {isTriangle && (
                <RegularPolygon
                    sides={3}
                    radius={35}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    rotation={30} // Orient it nicely
                />
            )}
            {isRectangle && (
                <Rect
                    width={100}
                    height={50}
                    offsetX={50}
                    offsetY={25}
                    cornerRadius={5}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                />
            )}
            {isSquare && (
                <Rect
                    width={60}
                    height={60}
                    offsetX={30}
                    offsetY={30}
                    cornerRadius={5}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                />
            )}

            <Text
                text={table.name}
                align="center"
                verticalAlign="middle"
                width={60}
                offsetX={30}
                y={-15}
                listening={false}
                fontStyle="bold"
            />
            <Text
                text={`${table.diners} סועדים`}
                y={5}
                width={60}
                offsetX={30}
                align="center"
                fontSize={11}
                listening={false}
            />
            {isLocked && (
                <Rect
                    x={0}
                    y={-38}
                    width={8}
                    height={8}
                    offsetX={4}
                    cornerRadius={2}
                    fill="#b06a6a"
                    listening={false}
                />
            )}
        </Group>
    );
};

export default CanvasTableMap;
